// src/components/VOCLevel.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles.css';

const VOCLevel = () => {
  const [voc, setVOC] = useState(null);
  const [co2, setCO2] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const navigate = useNavigate();

  const CHANNEL_ID = '3018449';
  const READ_API_KEY = 'WPIXJNON8E0WC5U0';

  const getThingSpeakUrl = () => {
    const baseUrl = `https://api.thingspeak.com/channels/${CHANNEL_ID}/feeds/last.json`;
    return READ_API_KEY ? `${baseUrl}?api_key=${READ_API_KEY}` : baseUrl;
  };

  useEffect(() => {
    const fetchVOCData = async () => {
      try {
        setConnectionStatus('Fetching...');
        const response = await fetch(getThingSpeakUrl());
        if (response.ok) {
          const data = await response.json();

          if (!data.created_at) {
            setConnectionStatus('Device Offline');
            setVOC(null);
            setCO2(null);
            setLastUpdate(null);
            return;
          }

          const lastUpdateTime = new Date(data.created_at);
          const now = new Date();
          const diffMinutes = (now - lastUpdateTime) / 1000 / 60;

          if (diffMinutes > 5) {
            setConnectionStatus('Device Offline');
            setVOC(null);
            setCO2(null);
            setLastUpdate(null);
            return;
          }

          const vocValue = parseFloat(data.field3);
          const co2Value = parseFloat(data.field8);

          if (!isNaN(vocValue)) setVOC(vocValue);
          else setVOC(null);

          if (!isNaN(co2Value)) setCO2(co2Value);
          else setCO2(null);

          setConnectionStatus('Connected');
          setLastUpdate(lastUpdateTime.toLocaleTimeString());
        } else {
          setConnectionStatus('Error Fetching');
        }
      } catch (error) {
        console.error('Error fetching VOC data:', error);
        setConnectionStatus('Error');
      }
    };

    fetchVOCData();
    const interval = setInterval(fetchVOCData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIndicator = () => {
    if (typeof voc !== 'number' || co2 === 0) return null;
    const inRange = voc < 500;
    return (
      <div className={`status-indicator ${inRange ? 'good' : 'bad'}`}>
        <span className="material-icons emoji">
          {inRange ? 'sentiment_satisfied_alt' : 'sentiment_dissatisfied'}
        </span>
        <span className="label">{inRange ? 'Good' : 'Bad'}</span>
      </div>
    );
  };

  const displayVOC =
    typeof voc === 'number' ? `${voc.toFixed(2)} ppb` : 'Loading...';

  return (
    <div className="main-content">
      <div className="voc-container">
        <h2>VOC Level 🌬️</h2>

        <div
          className="connection-status"
          style={{
            color: connectionStatus === 'Connected' ? '#22cf27ff' : '#f44336',
            fontSize: '14px',
            marginBottom: '10px',
          }}
        >
          <strong>ThingSpeak Status:</strong> {connectionStatus}
          {lastUpdate && (
            <span style={{ marginLeft: '15px' }}>Last Update: {lastUpdate}</span>
          )}
        </div>

        {connectionStatus === 'Device Offline' ? (
          <div
            style={{
              color: '#f44336',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              fontSize: '16px',
            }}
          >
            ⚠️ Device Offline or No Recent Data
          </div>
        ) : co2 === 0 ? (
          <div
            style={{
              color: '#f44336',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              fontSize: '16px',
            }}
          >
            ⚠️ Error in the sensor or sensor disconnected
          </div>
        ) : (
          <div className="status-card">
            {getStatusIndicator()}
            <span className="status-icon voc-icon">🌬️</span>
            <div className="status-info">
              <p style={{ fontSize: '18px' }}>VOC Concentration: {displayVOC}</p>
            </div>
          </div>
        )}

        <h3>VOC Ranges</h3>
        <div className="ranges">
          <div className="range-item">
            <span className="range-icon">✅</span>
            <p>Recommended: Below 500 ppb</p>
          </div>
          <div className="range-item">
            <span className="range-icon">✅</span>
            <p>Ideal: Below 100 ppb</p>
          </div>
        </div>

        {/* View Logs Button */}
        <button 
              className='ViewLogsButton' 
              onClick={() => navigate('/dashboard/data-history')}
        >
          View Logs
        </button>
      </div>
    </div>
  );
};

export default VOCLevel;
